/**
 * MessageList - Scrollable message list with date grouping
 */
import { useRef, useEffect, useMemo } from "react";
import { MessageBubble } from "./MessageBubble";
import { SystemMessage } from "./SystemMessage";
import type { ChatMessage } from "../types";

interface MessageListProps {
  messages: ChatMessage[];
  currentUserId?: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  resolveUrl?: (url: string | null) => string;
  className?: string;
}

interface MessageGroup {
  date: string;
  messages: ChatMessage[];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return date.toLocaleDateString(undefined, { 
    weekday: "short", 
    month: "short", 
    day: "numeric" 
  });
}

function groupMessagesByDate(messages: ChatMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let currentDate = "";

  for (const msg of messages) {
    const msgDate = new Date(msg.created_at).toDateString();
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groups.push({
        date: formatDate(msg.created_at),
        messages: [msg],
      });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
  }

  return groups;
}

export function MessageList({
  messages,
  currentUserId,
  isLoading,
  onLoadMore,
  hasMore,
  resolveUrl,
  className = "",
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleScroll = () => {
    if (!containerRef.current || !onLoadMore || !hasMore || isLoading) return;
    
    const { scrollTop } = containerRef.current;
    if (scrollTop < 100) {
      onLoadMore();
    }
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className={`flex-1 overflow-y-auto px-4 py-2 ${className}`}
    >
      {isLoading && (
        <div className="flex justify-center py-4">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {groupedMessages.map((group) => (
        <div key={group.date}>
          {/* Date separator */}
          <div className="flex items-center gap-3 py-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-medium">{group.date}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Messages in this group */}
          {group.messages.map((msg) => {
            const isOwn = msg.sender?.id === currentUserId;

            if (msg.message_type === "system") {
              return <SystemMessage key={msg.id} content={msg.content} />;
            }

            return (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwn={isOwn}
                resolveUrl={resolveUrl}
                onImageClick={(url) => window.open(url, "_blank")}
              />
            );
          })}
        </div>
      ))}

      <div ref={bottomRef} />
    </div>
  );
}
